import { categoryLabel, numericClassesForClassification } from './classification';
import { effectiveSymbols } from './effectiveSymbols';
import type { VectorLayerStyle, VectorSymbols } from './model';

export interface LegendEntry {
  readonly id: string;
  readonly label: string;
  readonly colour: string;
  readonly symbols: VectorSymbols;
}

function representativeColour(symbols: VectorSymbols): string {
  return symbols.point.fillColor || symbols.line.color || symbols.polygon.fillColor;
}

export function formatGraduatedNumber(value: number, compact = false): string {
  if (compact && Math.abs(value) >= 1_000_000_000) {
    return value.toLocaleString('en-AU', {
      notation: 'compact',
      maximumFractionDigits: 2,
    });
  }
  if (Number.isInteger(value)) return value.toLocaleString('en-AU');
  return value.toLocaleString('en-AU', { maximumFractionDigits: 6 });
}

const MAX_BOUNDARY_FRACTION_DIGITS = 6;

function effectivelyIntegral(value: number): boolean {
  return Math.abs(value - Math.round(value)) <= Number.EPSILON * Math.max(1, Math.abs(value)) * 8;
}

export function graduatedBoundaryFractionDigits(boundaries: readonly number[]): number {
  if (boundaries.every(effectivelyIntegral)) return 0;
  let minimumInterval = Number.POSITIVE_INFINITY;
  for (let index = 1; index < boundaries.length; index += 1) {
    const previous = boundaries[index - 1];
    const current = boundaries[index];
    if (previous === undefined || current === undefined) continue;
    const interval = Math.abs(current - previous);
    if (interval > 0 && interval < minimumInterval) minimumInterval = interval;
  }
  const distinguishingDigits = Number.isFinite(minimumInterval)
    ? Math.max(0, Math.ceil(-Math.log10(minimumInterval)))
    : 0;
  return Math.min(MAX_BOUNDARY_FRACTION_DIGITS, Math.max(2, distinguishingDigits));
}

export function createGraduatedBoundaryFormatter(
  boundaries: readonly number[],
): (value: number) => string {
  const maximumFractionDigits = graduatedBoundaryFractionDigits(boundaries);
  return (value) =>
    value.toLocaleString('en-AU', {
      maximumFractionDigits,
      useGrouping: true,
    });
}

export function graduatedClassLabel(
  lower: number,
  upper: number,
  includeUpper: boolean,
  formatNumber: (value: number) => string = formatGraduatedNumber,
): string {
  if (lower === upper) return formatNumber(lower);
  return includeUpper
    ? `${formatNumber(lower)} – ${formatNumber(upper)}`
    : `${formatNumber(lower)} – <${formatNumber(upper)}`;
}

export function deriveLegendEntries(
  layerName: string,
  style: VectorLayerStyle,
): readonly LegendEntry[] {
  const classification = style.classification;
  if (classification.mode === 'single') {
    return [
      {
        id: 'single',
        label: layerName,
        colour: style.symbols.polygon.fillColor,
        symbols: style.symbols,
      },
    ];
  }
  if (classification.mode === 'categorised') {
    return [
      ...classification.categories.map((category) => ({
        id: `category:${JSON.stringify(category.value)}`,
        label: category.symbol.legendLabel ?? categoryLabel(category.value),
        colour: representativeColour(effectiveSymbols(style.symbols, category.symbol)),
        symbols: effectiveSymbols(style.symbols, category.symbol),
      })),
      {
        id: 'category:other',
        label: classification.other.legendLabel ?? 'Other / unmatched',
        colour: representativeColour(effectiveSymbols(style.symbols, classification.other)),
        symbols: effectiveSymbols(style.symbols, classification.other),
      },
    ];
  }

  const formatBoundary = createGraduatedBoundaryFormatter(classification.boundaries);
  return [
    ...numericClassesForClassification(classification).map((numericClass, index) => ({
      id: `class:${index}`,
      label:
        numericClass.symbol.legendLabel ??
        graduatedClassLabel(
          numericClass.lower,
          numericClass.upper,
          numericClass.includeUpper,
          formatBoundary,
        ),
      colour: representativeColour(effectiveSymbols(style.symbols, numericClass.symbol)),
      symbols: effectiveSymbols(style.symbols, numericClass.symbol),
    })),
    {
      id: 'class:null',
      label: classification.nullSymbol.legendLabel ?? 'Null / non-numeric',
      colour: representativeColour(effectiveSymbols(style.symbols, classification.nullSymbol)),
      symbols: effectiveSymbols(style.symbols, classification.nullSymbol),
    },
  ];
}
